import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  TextField,
  Button,
} from '@finpay-development/shared-components';
import {
  Box,
  Grid,
  Paper,
  Typography,
  MenuItem,
} from '@mui/material';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import { getClientFacilities } from '../../implementation-specialist/state/facility/implementation-facility-thunk';
import { specialistFacilityService } from '../../implementation-specialist/services/implementation-facility-service';
import { addPatientToRecentPatientsIfNeeded } from '../../patient/state/patient-thunk';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { patientService } from '../../patient/services/patient-service';
import { showErrorStatus, showStatus } from '../../security/state/user-slice';
import { isPaidInFull } from '../hub-utils';
import { ImplementationFacility } from '../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';
import {
  EstimatesSearchModal
} from '../../estimates/components/estimate-search/estimates-search-modal';
import { PrePaymentErrorModal } from './pre-payment-error-modal';
import { useHubChannel } from '../hooks/use-hub-channel';
import {
  validatePrePaymentRequirements,
  PrePaymentRequirement
} from '../utils/pre-payment-validation';
import {RolePageNames} from '../../security/model/role-page-names';
import AccessControl from '../../security/components/access-control';
import { admissionsAdvisorService } from '../../admissions-advisor/services/admissions-advisor-services';
import type { Estimate } from '@finpay-estimation/estimation-types';
import { formatNumberToUSD } from '../../shared/calculators';
import { StatusColors } from '../../shared/enums';
import {
  getPatientStatusByStatusId,
  StatusType,
  WorkflowId,
} from '../../patient/components/misc/patient-status';
import { WorkflowMetaData } from '../../meta-data/models/metaData';

function normalizeWorkflowMetaNextSteps(row: Record<string, unknown> | undefined): string | null {
  if (!row) return null;
  const raw =
    typeof row.nextSteps === 'string'
      ? row.nextSteps
      : typeof row.next_steps === 'string'
        ? (row.next_steps as string)
        : '';
  const text = raw.trim();
  return text || null;
}

/** Substatus `nextSteps` when present; otherwise primary status `nextSteps` from metaData.workflow. */
function findWorkflowNextSteps(
  workflows: WorkflowMetaData[],
  workflowId: number | undefined,
  workflowStatusId: number | undefined,
  workflowSubStatusId: number | undefined,
): string | null {
  if (workflowId == null || workflowStatusId == null) {
    return null;
  }
  const wf = workflows.find((w) => w.workflowId === workflowId);
  if (!wf?.workflowStatus?.length) return null;
  const st = wf.workflowStatus.find((s) => s.workflowStatusId === workflowStatusId);
  if (!st) return null;

  const statusNext = normalizeWorkflowMetaNextSteps(st as Record<string, unknown>);

  if (workflowSubStatusId == null || workflowSubStatusId === 0) {
    return statusNext;
  }

  const sub = st.workflowSubStatus?.find((ss) => ss.workflowSubStatusId === workflowSubStatusId);
  const subNext = normalizeWorkflowMetaNextSteps(sub as Record<string, unknown> | undefined);

  return subNext ?? statusNext;
}

const IOC_OUT_OF_DATE_VS_ESTIMATE_PREFIX =
  'Warning: IOC is out of date, click Update IOC from estimate. ';

/** Min ms the linked estimate’s latest timestamp must be ahead of the IOC’s (avoids sub-second noise). */
const ESTIMATE_NEWER_THAN_IOC_THRESHOLD_MS = 30000;

/** Latest of parseable timestamps (ISO strings, Dates, or ms numbers). */
function maxTimestampMs(...values: unknown[]): number {
  let m = 0;
  for (const v of values) {
    if (v == null || v === '') continue;
    let t: number;
    if (v instanceof Date) t = v.getTime();
    else if (typeof v === 'string') t = Date.parse(v);
    else if (typeof v === 'number' && Number.isFinite(v)) t = v;
    else continue;
    if (!Number.isNaN(t)) m = Math.max(m, t);
  }
  return m;
}

/** Converted workflow (3): Cleared when substatus is set and not 30 or 32. */
function isHubIocClearedForDisplay(workflowId?: number, workflowSubStatusId?: number): boolean {
  return (
    workflowId === WorkflowId.Converted &&
    workflowSubStatusId != null &&
    workflowSubStatusId !== 0 &&
    workflowSubStatusId !== 30 &&
    workflowSubStatusId !== 32
  );
}

interface HubFormValues {
  patientName: string;
  facility: string;
  ioc: string;
}

const validationSchema = Yup.object({
  patientName: Yup.string().required('Please select a patient'),
  facility: Yup.string().optional(), // Hidden for now
  ioc: Yup.string().required('Please select an IOC'),
});

function HubView() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ patientId?: string; patientEncounterId?: string }>();
  const patientIdParam = params.patientId;
  const patientEncounterIdParam = params.patientEncounterId;
  const dispatch = useDispatch<AppDispatch>();

  // Parse patientId and encounterId from URL path - useParams can be unreliable in some route setups
  const urlIds = (() => {
    const match = location.pathname.match(/\/hub\/patient\/(\d+)(?:\/ioc\/(\d+))?/);
    if (match) {
      return {
        patientId: parseInt(match[1], 10),
        encounterId: match[2] ? parseInt(match[2], 10) : undefined,
      };
    }
    return null;
  })();

  const [patients, setPatients] = useState<any[]>([]);
  const [patientIocs, setPatientIocs] = useState<{
    id: number;
    date: string;
    display: string;
    workflowId?: number;
    workflowStatusId?: number;
    workflowSubStatusId?: number;
    baseDisplay: string;
    pfrPart: string;
    statusPart: string;
    statusColor: string;
    pfrEstimateId?: number;
    encounterInstanceCreateDt: string;
    /** Row `create_dt` (NOT NULL in DB); string used for estimate freshness compare */
    encounterCreateDt: string;
    encounterLastUpdateDt?: string;
  }[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isLoadingIocs, setIsLoadingIocs] = useState(false);

  // Pre-payment validation state
  const [isPrePaymentModalOpen, setIsPrePaymentModalOpen] = useState(false);
  const [prePaymentRequirements, setPrePaymentRequirements] = useState<PrePaymentRequirement[]>([]);
  const [isValidatingPrePayment, setIsValidatingPrePayment] = useState(false);

  const facilities = useSelector((state: RootState) => 
    state.implementationContext.implementationFacility.facilities
  ).flat() as ImplementationFacility[];

  const allowedClient = useSelector((state: RootState) => 
    state.userContext.userProfile.allowedClients[0]
  );

  const recentPatients = useSelector((state: RootState) => 
    state.patientContext.recentPatients
  );

  const userId = useSelector((state: RootState) => 
    state.userContext.userProfile.userId
  );

  const selectedEncounter = useSelector((state: RootState) => state.patientContext.selectedEncounter);
  const selectedPatient = useSelector((state: RootState) => state.patientContext.selectedPatient);

  const loadIocsGenerationRef = useRef(0);
  /** One in-flight auto-create per patient (avoids duplicate IOCs from Strict Mode / effect re-runs). */
  const pendingIocAutoCreateRef = useRef<Map<number, Promise<PatientEncounter[]>>>(new Map());
  const linkedEstimateFreshnessFetchGenRef = useRef(0);
  const linkedEstimateRawDatesRef = useRef<{ createDt: unknown; lastUpdateDt: unknown } | null>(null);
  const [linkedEstimateMaxMs, setLinkedEstimateMaxMs] = useState<number | null>(null);

  // Set channel from URL or default to fms
  useHubChannel();

  const getPatientAndEncounterIds = (
    patientIdFromForm?: string
  ): { patientId: number; encounterId?: number } | null => {
    // Prioritize URL - parse from pathname directly (reliable when useParams has issues)
    if (urlIds && urlIds.patientId) {
      return {
        patientId: urlIds.patientId,
        encounterId: urlIds.encounterId,
      };
    }
    // Fallback to useParams
    if (patientIdParam && patientEncounterIdParam) {
      const pid = parseInt(patientIdParam, 10);
      const eid = parseInt(patientEncounterIdParam, 10);
      if (!isNaN(pid) && !isNaN(eid)) return { patientId: pid, encounterId: eid };
    }
    if (patientIdParam) {
      const pid = parseInt(patientIdParam, 10);
      if (!isNaN(pid)) return { patientId: pid };
    }
    if (selectedEncounter?.patientId && selectedEncounter?.patientEncounterId) {
      return {
        patientId: selectedEncounter.patientId,
        encounterId: selectedEncounter.patientEncounterId,
      };
    }
    if (selectedPatient?.patientId) {
      const firstEncounter = selectedPatient.patientEncounters?.[0];
      if (firstEncounter?.patientEncounterId) {
        return {
          patientId: selectedPatient.patientId,
          encounterId: firstEncounter.patientEncounterId,
        };
      }
      return { patientId: selectedPatient.patientId };
    }
    if (patientIdFromForm) {
      const pid = parseInt(patientIdFromForm, 10);
      if (!isNaN(pid)) return { patientId: pid };
    }
    // Fallback: use first patient from recent patients list if not empty
    const firstRecentPatient = recentPatients.find(
      (p) => p.patientId && p.patientName
    );
    if (firstRecentPatient) {
      return { patientId: firstRecentPatient.patientId };
    }
    return null;
  };

  useEffect(() => {
    if (allowedClient?.clientId) {
      dispatch(getClientFacilities(allowedClient.clientId));
    }
  }, [dispatch, allowedClient]);

  // Sync URL patient into recents when route/user is ready. List membership is handled inside the thunk
  // (avoids re-running on every recentPatients refresh / GET).
  useEffect(() => {
    if (patientIdParam && userId && userId > 0) {
      const pid = parseInt(patientIdParam, 10);
      if (!isNaN(pid)) {
        dispatch(addPatientToRecentPatientsIfNeeded(pid));
      }
    }
  }, [dispatch, patientIdParam, userId]);

  // Get first valid patient from Redux recent patients
  const getFirstValidPatient = (): string => {
    const validPatient = recentPatients.find(
      (patient) => patient.patientId && patient.patientName
    );
    return validPatient ? validPatient.patientId.toString() : '';
  };

  const initialValues: HubFormValues = useMemo(() => {
    const patientIdFromUrl = patientIdParam ?? '';
    const firstPatientId = patientIdFromUrl || getFirstValidPatient();
    const iocFromUrl = patientEncounterIdParam ?? '';
    return {
      patientName: firstPatientId,
      facility: '',
      ioc: iocFromUrl,
    };
  }, [patientIdParam, patientEncounterIdParam, recentPatients]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Handle form submission
      console.log('Form submitted:', values);
    },
  });

  const selectedIocForWorkflowSummary = useMemo(() => {
    const raw = formik.values.ioc;
    if (!raw) return null;
    const encounterId = parseInt(raw, 10);
    if (Number.isNaN(encounterId)) return null;
    return patientIocs.find((ioc) => ioc.id === encounterId) ?? null;
  }, [formik.values.ioc, patientIocs]);

  const workflowMetaList = useSelector((state: RootState) => state.metaData.workflow);

  const selectedIocWorkflowNextStepsBase = useMemo(() => {
    const ioc = selectedIocForWorkflowSummary;
    if (!ioc) return null;
    return findWorkflowNextSteps(
      workflowMetaList,
      ioc.workflowId,
      ioc.workflowStatusId,
      ioc.workflowSubStatusId,
    );
  }, [selectedIocForWorkflowSummary, workflowMetaList]);

  useEffect(() => {
    const ioc = selectedIocForWorkflowSummary;
    const estimateId = ioc?.pfrEstimateId;
    if (!ioc || estimateId == null || estimateId <= 0) {
      linkedEstimateRawDatesRef.current = null;
      setLinkedEstimateMaxMs(null);
      return;
    }
    const gen = ++linkedEstimateFreshnessFetchGenRef.current;
    setLinkedEstimateMaxMs(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await admissionsAdvisorService.callV3EstimateGet(-2, estimateId);
        if (cancelled || gen !== linkedEstimateFreshnessFetchGenRef.current) return;
        if (res.hasErrors || !res.entity) {
          linkedEstimateRawDatesRef.current = null;
          setLinkedEstimateMaxMs(null);
          return;
        }
        const est = res.entity as Estimate;
        const maxMs = maxTimestampMs(est.createDt, est.lastUpdateDt);
        if (cancelled || gen !== linkedEstimateFreshnessFetchGenRef.current) return;
        if (maxMs > 0) {
          linkedEstimateRawDatesRef.current = {
            createDt: est.createDt,
            lastUpdateDt: est.lastUpdateDt,
          };
          setLinkedEstimateMaxMs(maxMs);
        } else {
          linkedEstimateRawDatesRef.current = null;
          setLinkedEstimateMaxMs(null);
        }
      } catch {
        if (!cancelled && gen === linkedEstimateFreshnessFetchGenRef.current) {
          linkedEstimateRawDatesRef.current = null;
          setLinkedEstimateMaxMs(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedIocForWorkflowSummary?.id, selectedIocForWorkflowSummary?.pfrEstimateId]);

  const iocIsOutOfDateVsLinkedEstimate = useMemo(() => {
    const ioc = selectedIocForWorkflowSummary;
    if (!ioc?.pfrEstimateId || linkedEstimateMaxMs == null || linkedEstimateMaxMs <= 0) {
      return false;
    }
    const encounterMaxMs = maxTimestampMs(ioc.encounterCreateDt, ioc.encounterLastUpdateDt);
    if (encounterMaxMs <= 0) return false;
    return linkedEstimateMaxMs - encounterMaxMs >= ESTIMATE_NEWER_THAN_IOC_THRESHOLD_MS;
  }, [selectedIocForWorkflowSummary, linkedEstimateMaxMs]);

  useEffect(() => {
    const ioc = selectedIocForWorkflowSummary;
    if (!ioc?.pfrEstimateId || linkedEstimateMaxMs == null || linkedEstimateMaxMs <= 0) return;
    const encounterMaxMs = maxTimestampMs(ioc.encounterCreateDt, ioc.encounterLastUpdateDt);
    if (encounterMaxMs <= 0) return;
    const diffMs = linkedEstimateMaxMs - encounterMaxMs;
    const estRaw = linkedEstimateRawDatesRef.current;
    console.log('[hub IOC vs estimate dates]', {
      iocId: ioc.id,
      pfrEstimateId: ioc.pfrEstimateId,
      iocCreateDt: ioc.encounterCreateDt,
      iocInstanceCreateDt: ioc.encounterInstanceCreateDt,
      iocLastUpdateDt: ioc.encounterLastUpdateDt,
      iocLatestMs: encounterMaxMs,
      iocLatestIso: new Date(encounterMaxMs).toISOString(),
      estimateCreateDt: estRaw?.createDt,
      estimateLastUpdateDt: estRaw?.lastUpdateDt,
      estimateLatestMs: linkedEstimateMaxMs,
      estimateLatestIso: new Date(linkedEstimateMaxMs).toISOString(),
      diffMs,
      diffSeconds: diffMs / 1000,
      thresholdMs: ESTIMATE_NEWER_THAN_IOC_THRESHOLD_MS,
      outOfDateByThreshold: diffMs >= ESTIMATE_NEWER_THAN_IOC_THRESHOLD_MS,
    });
  }, [selectedIocForWorkflowSummary, linkedEstimateMaxMs]);

  const selectedIocWorkflowNextSteps = useMemo(() => {
    const base = selectedIocWorkflowNextStepsBase;
    if (!iocIsOutOfDateVsLinkedEstimate) return base;
    return base != null
      ? IOC_OUT_OF_DATE_VS_ESTIMATE_PREFIX + base
      : IOC_OUT_OF_DATE_VS_ESTIMATE_PREFIX.trimEnd();
  }, [selectedIocWorkflowNextStepsBase, iocIsOutOfDateVsLinkedEstimate]);

  // Load IOCs when patient changes; re-run when default facility becomes available (for auto-create after facilities load).
  useEffect(() => {
    const patientId = formik.values.patientName || patientIdParam || getFirstValidPatient();
    if (patientId) {
      loadIocs(patientId, '');
    }
  }, [patientIdParam, recentPatients, formik.values.patientName]);


  const handleNewPatient = () => {
    // Navigate to new patient creation
    navigate('/hub/patient');
  };

  const handleFacilityChange = async (facilityId: string) => {
    formik.setFieldValue('facility', facilityId, false);
    formik.setFieldValue('ioc', '', false);
    setPatientIocs([]);

    if (facilityId && formik.values.patientName) {
      // Load IOCs for selected patient and facility
      await loadIocs(formik.values.patientName, facilityId);
    }
  };

  const handlePatientChange = async (patientId: string) => {
    formik.setFieldValue('patientName', patientId, false);
    formik.setFieldValue('ioc', '', false);
    setPatientIocs([]);

    if (patientId) {
      // Load IOCs for selected patient (facility hidden for now)
      await loadIocs(patientId, '');
    }
  };

  const getIocStatusColor = (enc: PatientEncounter): string => {
    const w = enc.workflow;
    const sub = w?.workflowSubStatus;
    const subDesc = sub?.workflowSubStatusDesc?.trim().toLowerCase();
    if (sub?.workflowSubStatusId === 26 || subDesc === 'on hold') {
      return StatusColors.error;
    }
    const primaryDesc = w?.workflowStatus?.workflowStatusDesc?.trim().toLowerCase();
    if (primaryDesc === 'on hold') {
      return StatusColors.error;
    }
    const workflowStatusId = w?.workflowStatus?.workflowStatusId;
    if (workflowStatusId == null || workflowStatusId === 13) return StatusColors.blue; // New
    const wfId = w?.workflowId;
    if (wfId !== WorkflowId.Preconverted && wfId !== WorkflowId.Converted) return StatusColors.gray;
    const status = getPatientStatusByStatusId(workflowStatusId, StatusType['primary-status'], wfId as WorkflowId);
    return status?.color ?? StatusColors.gray;
  };

  const loadIocs = async (patientId: string, _facilityId: string) => {
    if (!patientId) return;

    const pid = parseInt(patientId, 10);
    if (isNaN(pid)) return;

    const myGen = ++loadIocsGenerationRef.current;

    const isStale = () => myGen !== loadIocsGenerationRef.current;

    const buildIocList = (encounters: PatientEncounter[]) =>
      encounters.map((enc: PatientEncounter) => {
        const date = enc.instanceCreateDt
          ? new Date(enc.instanceCreateDt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : '';
        const facility = enc.facilityName || '';
        const workflowId = enc.workflow?.workflowId;
        const baseDisplay = [facility, date].filter(Boolean).join(' - ');
        const pfrValue = enc.adjustedPfrAmt != null && !Number.isNaN(enc.adjustedPfrAmt)
          ? enc.adjustedPfrAmt
          : (enc.pfrAmt != null && !Number.isNaN(enc.pfrAmt) ? enc.pfrAmt : null);
        const pfrFormatted = pfrValue != null ? formatNumberToUSD(pfrValue) : '';
        const workflowStatusDesc =
          enc.workflow?.workflowStatus?.workflowStatusDesc?.trim() ?? '';
        const subStatusDesc = enc.workflow?.workflowSubStatus?.workflowSubStatusDesc;
        const statusPart = [
          workflowStatusDesc || undefined,
          ...(subStatusDesc && subStatusDesc !== 'None' ? [subStatusDesc] : []),
        ]
          .filter(Boolean)
          .join(' - ');
        const pfrPart = pfrFormatted;
        const fullSuffix = [pfrPart].filter(Boolean).join(' - ');
        const display = `${baseDisplay || 'IOC'}${fullSuffix ? ` - ${fullSuffix}` : ''}`;
        const workflowStatusId = enc.workflow?.workflowStatus?.workflowStatusId;
        const workflowSubStatusId = enc.workflow?.workflowSubStatus?.workflowSubStatusId;
        return {
          id: enc.patientEncounterId,
          date: enc.instanceCreateDt || '',
          display,
          workflowId,
          workflowStatusId,
          workflowSubStatusId,
          baseDisplay: baseDisplay || 'IOC',
          pfrPart,
          statusPart,
          statusColor: getIocStatusColor(enc),
          pfrEstimateId: enc.pfrEstimateId,
          encounterInstanceCreateDt: enc.instanceCreateDt || '',
          encounterCreateDt: enc.createDt,
          encounterLastUpdateDt: enc.lastUpdateDt,
        };
      });

    setIsLoadingIocs(true);
    try {
      let response = await patientService.getPatientInstancesOfCare({
        patientId: pid,
        limit: 100,
        offset: 0,
      });

      if (isStale()) return;

      if (response.hasErrors || !response.entity) {
        setPatientIocs([]);
        formik.setFieldValue('ioc', '', false);
        return;
      }

      let encounters = (Array.isArray(response.entity) ? response.entity : []) as PatientEncounter[];

      // Same as new-patient flow: no IOCs → create one via AA encounter API. Client = patient’s clientId.
      if (encounters.length === 0) {
        const runAutoCreate = async (): Promise<PatientEncounter[]> => {
          const patientRes = await patientService.getPatient(pid);
          if (patientRes.hasErrors || !patientRes.entity) {
            dispatch(showErrorStatus(patientRes.errorMessage || 'Could not load patient. Cannot create an IOC.'));
            return [];
          }
          const patientClientId =
            patientRes.entity.clientId != null && patientRes.entity.clientId !== ''
              ? Number(patientRes.entity.clientId)
              : undefined;
          if (!patientClientId || Number.isNaN(patientClientId)) {
            dispatch(showErrorStatus('Patient has no client. Cannot create an IOC for this patient.'));
            return [];
          }

          const facilitiesForPatientClient = await specialistFacilityService.getClientFacilities(patientClientId);
          if (facilitiesForPatientClient.hasErrors || facilitiesForPatientClient.entity == null) {
            dispatch(
              showErrorStatus(
                'Could not load facilities for the patient’s client. Cannot create an IOC for this patient.',
              ),
            );
            return [];
          }
          const raw = facilitiesForPatientClient.entity;
          let flatFacilities: ImplementationFacility[] = [];
          if (Array.isArray(raw)) {
            flatFacilities =
              raw.length > 0 && Array.isArray((raw as unknown[])[0])
                ? (raw as ImplementationFacility[][]).flat()
                : (raw as ImplementationFacility[]);
          } else if (raw && typeof raw === 'object') {
            const arr = (raw as { groupedFacilities?: ImplementationFacility[][] }).groupedFacilities;
            flatFacilities = Array.isArray(arr) ? arr.flat() : [];
          }
          const firstFacility = flatFacilities[0];
          if (!firstFacility?.facilityId) {
            dispatch(
              showErrorStatus(
                'No facility found for the patient’s client. Cannot create an IOC—add a facility or use New Patient.',
              ),
            );
            return [];
          }

          const clientId = patientClientId;
          const facilityId = firstFacility.facilityId;

          const riskClassId = 1;
          const timingRiskId = 1;
          const payorRiskId = 1;

          const createResponse = await admissionsAdvisorService.createAAPatientIoc(
            {
              patientId: pid,
              clientId,
              facilityId,
              pfrAmt: undefined,
              riskClass: { riskClassId },
              timingRisk: { timingRiskId },
              payorRisk: { payorRiskId },
            },
            0,
          );

          if (createResponse.hasErrors) {
            dispatch(
              showErrorStatus(
                createResponse.errorMessage ||
                  'Error creating patient instance of care. Please reach out to FinPay support if this problem persists.',
              ),
            );
            return [];
          }

          const refetch = await patientService.getPatientInstancesOfCare({
            patientId: pid,
            limit: 100,
            offset: 0,
          });

          let next: PatientEncounter[] = [];
          if (!refetch.hasErrors && refetch.entity) {
            next = (Array.isArray(refetch.entity) ? refetch.entity : []) as PatientEncounter[];
          }
          const createdEnc = createResponse.entity as PatientEncounter | undefined;
          if (next.length === 0 && createdEnc?.patientEncounterId) {
            next = [createdEnc];
          }
          return next;
        };

        let createPromise = pendingIocAutoCreateRef.current.get(pid);
        if (!createPromise) {
          createPromise = runAutoCreate().finally(() => {
            pendingIocAutoCreateRef.current.delete(pid);
          });
          pendingIocAutoCreateRef.current.set(pid, createPromise);
        }

        encounters = await createPromise;

        if (isStale()) return;

        if (encounters.length === 0) {
          setPatientIocs([]);
          formik.setFieldValue('ioc', '', false);
          return;
        }
      }

      if (isStale()) return;

      const iocList = buildIocList(encounters).sort((a, b) => b.id - a.id);

      setPatientIocs(iocList);

      const encounterIdFromUrl =
        urlIds?.encounterId ?? (patientEncounterIdParam ? parseInt(patientEncounterIdParam, 10) : undefined);
      const selectedId =
        encounterIdFromUrl && iocList.some((i) => i.id === encounterIdFromUrl)
          ? encounterIdFromUrl.toString()
          : (iocList[0]?.id?.toString() ?? '');
      formik.setFieldValue('ioc', selectedId, false);

      if (iocList.length > 0 && selectedId) {
        const urlNeedsUpdate =
          !urlIds || urlIds.patientId !== pid || urlIds.encounterId !== parseInt(selectedId, 10);
        if (urlNeedsUpdate) {
          navigate(`/hub/patient/${pid}/ioc/${selectedId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error loading IOCs:', error);
      setPatientIocs([]);
      formik.setFieldValue('ioc', '', false);
    } finally {
      if (!isStale()) {
        setIsLoadingIocs(false);
      }
    }
  };

  const validateAndNavigate = useCallback(async (targetPath: string, patientId: number, encounterId: number) => {
    setIsValidatingPrePayment(true);

    try {
      // Use existing Redux data if it matches the current selection, otherwise fetch from API
      const hasMatchingPatient = selectedPatient?.patientId === patientId && selectedPatient?.contact;
      const hasMatchingEncounter = selectedEncounter?.patientEncounterId === encounterId;

      let patient: PatientViewModel | null = hasMatchingPatient ? selectedPatient : null;
      let encounter: PatientEncounter | null = hasMatchingEncounter ? selectedEncounter : null;

      // Only fetch what we don't already have
      const fetchPromises: Promise<any>[] = [];
      if (!patient) {
        fetchPromises.push(patientService.getPatient(patientId).then(res => {
          patient = res.hasErrors ? null : res.entity;
        }));
      }
      if (!encounter) {
        fetchPromises.push(patientService.getPatientInstanceOfCare({ patientId, encounterId }).then(res => {
          encounter = res.hasErrors ? null : res.entity;
        }));
      }

      if (fetchPromises.length > 0) {
        await Promise.all(fetchPromises);
      }

      const validationResult = validatePrePaymentRequirements({
        encounter,
        patient,
        patientId,
        encounterId,
        isLoading: false,
      });

      if (validationResult.isValid) {
        navigate(targetPath);
      } else {
        setPrePaymentRequirements(validationResult.requirements);
        setIsPrePaymentModalOpen(true);
      }
    } catch (error) {
      console.error('Error validating pre-payment requirements:', error);
      dispatch(showErrorStatus('Error validating payment requirements. Please try again.'));
    } finally {
      setIsValidatingPrePayment(false);
    }
  }, [navigate, dispatch, selectedPatient, selectedEncounter]);

  const handlePrePaymentModalClose = () => {
    setIsPrePaymentModalOpen(false);
    setPrePaymentRequirements([]);
  };

  const handleFixClick = (url: string) => {
    setIsPrePaymentModalOpen(false);
    navigate(url);
  };

  const handleTakePayment = () => {
    const ids = getPatientAndEncounterIds(formik.values.patientName);
    if (ids) {
      if (ids.encounterId !== undefined) {
        validateAndNavigate(
          `/hub/patient/${ids.patientId}/ioc/${ids.encounterId}/pay`,
          ids.patientId,
          ids.encounterId
        );
      } else {
        navigate(`/hub/patient/${ids.patientId}`);
      }
    } else {
      dispatch(showErrorStatus('Please select a patient first.'));
    }
  };

  const handleSetupPaymentProgram = async () => {
    const ids = getPatientAndEncounterIds(formik.values.patientName);
    if (ids) {
      if (ids.encounterId !== undefined) {
        // Check if program exists and is fully paid before pre-payment validation
        const hasMatchingEncounter = selectedEncounter?.patientEncounterId === ids.encounterId;
        let encounter: PatientEncounter | null = hasMatchingEncounter ? selectedEncounter : null;

        if (!encounter) {
          try {
            const res = await patientService.getPatientInstanceOfCare({ 
              patientId: ids.patientId, 
              encounterId: ids.encounterId 
            });
            encounter = res.hasErrors ? null : res.entity;
          } catch (error) {
            console.error('Error fetching encounter:', error);
          }
        }

        // const program = encounter?.patientPaymentProgram?.[0];
        // if (program && isPaidInFull(program)) {
        //   dispatch(showStatus('Payment program is fully paid.'));
        //   return;
        // }

        validateAndNavigate(
          `/hub/patient/${ids.patientId}/ioc/${ids.encounterId}/program`,
          ids.patientId,
          ids.encounterId
        );
      } else {
        navigate(`/hub/patient/${ids.patientId}`);
      }
    } else {
      dispatch(showErrorStatus('Please select a patient first.'));
    }
  };

  const formatDobAsMDYYYY = (isoDateString: string): string => {
    //Birthday is in YYYY-MM-DD format
    const arr = isoDateString.split('-')
    if(arr.length === 3){
      return `${Number(arr[1])}-${Number(arr[2])}-${arr[0]}`
    }else{
      return ""
    }
  };

  const selectedIocWorkflowId = patientIocs.find(
    (ioc) => ioc.id.toString() === formik.values.ioc
  )?.workflowId;

  const showSetupProgram = selectedIocWorkflowId === 2;

  return (
    <main test-id="hub-view">
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 950, mx: 'auto' }} test-id="hub-form-paper">
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Payment Hub
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
                {/* Recent Patients Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, '& .MuiInputBase-root': { height: 56 } }}>
                      <TextField
                        select
                        error={formik.touched.patientName && !!formik.errors.patientName}
                        label="Recent Patients"
                        name="patientName"
                        value={formik.values.patientName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePatientChange(e.target.value)}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.patientName ? formik.errors.patientName : undefined}
                        test-id="hub-patient-name"
                      >
                        <MenuItem value="">
                          <em>Select Patient</em>
                        </MenuItem>
                        {recentPatients
                          .filter((patient) => patient.patientId && patient.patientName)
                          .map((patient) => {
                            const dobFormatted = patient.birthDate
                              ? formatDobAsMDYYYY(patient.birthDate)
                              : '';
                            return (
                              <MenuItem key={patient.patientId} value={patient.patientId.toString()}>
                                {`${patient.patientName}${dobFormatted ? ` - ${dobFormatted}` : ''}`}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    </Box>
                    <EstimatesSearchModal
                        renderSearchPatient={
                          (openOnClick) => {
                            return <Button
                                type="secondary"
                                onClick={openOnClick}
                                disabled={isLoadingPatients}
                                test-id="hub-search-patient"
                                style={{ height: 56 }}
                            >
                              Search
                            </Button>
                          }
                        }
                        onClickConfig={{
                          patient: (patientId: number) => {
                            window.open(`${window.location.origin}/hub/patient/${patientId}`, '_blank', )
                          },
                          ioc: (patientId: number, encounterId: number) => window.open(`${window.location.origin}/hub/patient/${patientId}/ioc/${encounterId}`, '_blank', ),
                          vob: (patientId: number) => {
                            window.open(`${window.location.origin}/hub/patient/${patientId}`, '_blank', )
                          },
                          estimate: (patientId: number, encounterId?: number) => {
                            if(encounterId){
                              window.open(`${window.location.origin}/hub/patient/${patientId}/ioc/${encounterId}`, '_blank',)
                            }else{
                              window.open(`${window.location.origin}/hub/patient/${patientId}`, '_blank', )
                            }
                          }
                        }}


                    />
                    <AccessControl
                        rolePageName={RolePageNames.PaymentHub}
                        actionName="Add New Patient"
                        renderNoAccess={() => (
                            <Button
                                type="secondary"
                                onClick={handleNewPatient}
                                test-id="hub-new-patient"
                                style={{ height: 56 }}
                                disabled
                            >
                              New Patient
                            </Button>
                        )}
                    >
                      <Button
                          type="secondary"
                          onClick={handleNewPatient}
                          test-id="hub-new-patient"
                          style={{ height: 56 }}
                      >
                        New Patient
                      </Button>
                    </AccessControl>
                  </Box>
                </Grid>

                {/* Facility Section - Hidden for now */}
                {/* <Grid item xs={12}>
                  <TextField
                    select
                    error={formik.touched.facility && !!formik.errors.facility}
                    label="Facility"
                    name="facility"
                    value={formik.values.facility}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFacilityChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    helperText={formik.touched.facility ? formik.errors.facility : undefined}
                    test-id="hub-facility"
                  >
                    <MenuItem value="">
                      <em>Select Facility</em>
                    </MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem key={facility.facilityId} value={facility.facilityId.toString()}>
                        {facility.facilityName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid> */}

                {/* IOC Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      columnGap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <TextField
                        select
                        error={formik.touched.ioc && !!formik.errors.ioc}
                        label="IOC"
                        name="ioc"
                        value={formik.values.ioc}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          formik.handleChange(e);
                          const patientId = formik.values.patientName || patientIdParam;
                          const iocId = e.target.value;
                          if (patientId && iocId) {
                            navigate(`/hub/patient/${patientId}/ioc/${iocId}`, { replace: true });
                          }
                        }}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.ioc ? formik.errors.ioc : undefined}
                        disabled={!formik.values.patientName || isLoadingIocs}
                        test-id="hub-ioc"
                      >
                      <MenuItem value="">
                        <em>Select IOC</em>
                      </MenuItem>
                      {patientIocs.map((ioc) => (
                        <MenuItem key={ioc.id} value={ioc.id.toString()}>
                          {ioc.baseDisplay}
                          {ioc.pfrPart ? ` - ${ioc.pfrPart}` : ''}
                        </MenuItem>
                      ))}
                      </TextField>
                    </Box>
                    <Button
                      type="secondary"
                      onClick={() => {
                        const ids = getPatientAndEncounterIds(formik.values.patientName);
                        if (ids?.patientId && formik.values.ioc) {
                          navigate(`/specialist/dashboard/${ids.patientId}?encounter=${formik.values.ioc}`);
                        }
                      }}
                      disabled={!formik.values.patientName || !formik.values.ioc || isLoadingIocs}
                      test-id="hub-ioc-view"
                      style={{ height: 56 }}
                    >
                      View
                    </Button>
                      {selectedIocForWorkflowSummary ? (
                        <Typography
                          variant="body2"
                          sx={{
                            gridColumn: '1 / -1',
                            pl: 0.5,
                            pr: 0.5,
                            pt: 1.5,
                            pb: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            /* Explicit size: theme variants can disagree with px size */
                            fontSize: '1.75rem',
                            lineHeight: 1.45,
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                            alignItems: 'baseline',
                            columnGap: 2,
                          }}
                          component="div"
                          test-id="hub-ioc-workflow-summary"
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              flexWrap: 'wrap',
                              alignItems: 'baseline',
                              gap: 0.5,
                              minWidth: 0,
                            }}
                          >
                            {selectedIocForWorkflowSummary.statusPart ? (
                              <span
                                style={{
                                  color: selectedIocForWorkflowSummary.statusColor,
                                  fontWeight: 'bold',
                                }}
                              >
                                {selectedIocForWorkflowSummary.statusPart}
                              </span>
                            ) : (
                              <Box component="span" sx={{ color: 'text.secondary' }}>
                                —
                              </Box>
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              textAlign: 'right',
                              justifySelf: 'end',
                              whiteSpace: 'nowrap',
                            }}
                            test-id="hub-ioc-cleared-indicator"
                          >
                            {isHubIocClearedForDisplay(
                              selectedIocForWorkflowSummary.workflowId,
                              selectedIocForWorkflowSummary.workflowSubStatusId,
                            ) ? (
                              <Box
                                component="span"
                                sx={{ color: StatusColors.success, fontWeight: 600 }}
                              >
                                Cleared
                              </Box>
                            ) : (
                              <Box component="span" sx={{ color: 'text.primary' }}>
                                Not Cleared
                              </Box>
                            )}
                          </Box>
                        </Typography>
                      ) : null}
                      {selectedIocWorkflowNextSteps ? (
                        <Typography
                          variant="body2"
                          textAlign="left"
                          sx={{
                            gridColumn: '1 / -1',
                            pl: 0.5,
                            pr: 0.5,
                            pt: 2,
                            pb: 0,
                            fontSize: '2rem',
                            lineHeight: '2.6rem',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                          }}
                          component="div"
                          test-id="hub-ioc-workflow-next-steps"
                        >
                          {selectedIocWorkflowNextSteps}
                        </Typography>
                      ) : null}
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ width: '100%' }}>
                      <AccessControl
                          rolePageName={RolePageNames.PaymentHub}
                          actionName="Take a Payment"
                          renderNoAccess={() => (
                              <></>
                          )}
                      >
                        <Button
                            type="primary"
                            onClick={handleTakePayment}
                            test-id="hub-take-payment"
                            style={{
                              width: '100%',
                              paddingTop: '1rem',
                              paddingBottom: '1rem',
                              fontSize: '2rem',
                              fontWeight: 600
                            }}
                        >
                          Take a Payment
                        </Button>
                      </AccessControl>
                    </Box>
                    {showSetupProgram && (
                      <Box sx={{ width: '100%' }}>
                        <AccessControl
                            rolePageName={RolePageNames.PaymentHub}
                            actionName="Set Up Payment Program"
                            renderNoAccess={() => (
                                <></>
                            )}
                        >
                          <Button
                              type="primary"
                              onClick={handleSetupPaymentProgram}
                              disabled={isValidatingPrePayment}
                              loading={isValidatingPrePayment}
                              test-id="hub-setup-payment-program"
                              style={{
                                width: '100%',
                                paddingTop: '1rem',
                                paddingBottom: '1rem',
                                fontSize: '2rem',
                                fontWeight: 600
                              }}
                          >
                            Set Up Payment Program
                          </Button>
                        </AccessControl>
                      </Box>
                    )}
                  </Box>
                </Grid>
            </Grid>
          </form>
        </Paper>
      </div>

      <PrePaymentErrorModal
        isOpen={isPrePaymentModalOpen}
        handleModalClose={handlePrePaymentModalClose}
        requirementList={prePaymentRequirements}
        isLoading={isValidatingPrePayment}
        onFixClick={handleFixClick}
      />
    </main>
  );
}

export default HubView;
